import {
  dot,
  float,
  floor,
  Fn,
  fract,
  mix,
  smoothstep,
  vec2,
  vec3,
} from 'three/tsl';

// NO sin() IN THIS HASH, on purpose (Dave Hoskins, "Hash without Sine").
//
// It used to be the classic `fract(sin(dot(p, k)) * 43758.5)`. The power
// ring scrolls its warp noise by an ever-growing clock, so after a few
// minutes `dot(p, k)` sits in the 1e4..1e5 range -- where GPU sin() (fast
// math on Metal / D3D) loses its range reduction, the `* 43758` magnifies
// the error, and neighbouring lattice cells get the same gradient. The
// warp then goes flat per cell and the field shows as a grid of big square
// blocks. This one only multiplies and fract()s, so it degrades gracefully
// with magnitude instead of collapsing.
export const hash22 = Fn(([p]: [any]) => {
  const p3: any = fract(vec3(p.x, p.y, p.x).mul(vec3(0.1031, 0.103, 0.0973)));
  const mixed: any = p3.add(dot(p3, p3.yzx.add(33.33)));

  return fract(mixed.xx.add(mixed.yz).mul(mixed.zy)).mul(2).sub(1);
});

export const perlinNoise = Fn(([p]: [any]) => {
  // Annotated `any` throughout -- TS resolves an overloaded TSL function's
  // return type from its FIRST matching signature when the argument is
  // `any` rather than propagating `any` through, which for floor()/
  // fract() here would otherwise silently pick a vector overload instead
  // of the one actually needed a step later (`.x`/`.y` access on it).
  const iuv: any = floor(p);
  const fuv: any = fract(p);
  const blur: any = smoothstep(0, 1, fuv);

  const bl = vec2(0, 0);
  const br = vec2(1, 0);
  const tl = vec2(0, 1);
  const tr = vec2(1, 1);

  const bln: any = hash22(iuv.add(bl));
  const brn: any = hash22(iuv.add(br));
  const tln: any = hash22(iuv.add(tl));
  const trn: any = hash22(iuv.add(tr));

  const b = mix(dot(bln, fuv.sub(bl)), dot(brn, fuv.sub(br)), blur.x);
  const t = mix(dot(tln, fuv.sub(tl)), dot(trn, fuv.sub(tr)), blur.x);

  return mix(b, t, blur.y);
});

// Octave count is a JS-level loop (unrolled at material-build time), since
// it changes the shape of the generated shader graph and so can't be a
// live uniform.
export const fbm = (coord: any, octaves: number) => {
  let value: any = float(0);
  let amplitude = 0.5;
  let sample = coord;

  for (let i = 0; i < octaves; i++) {
    value = value.add(perlinNoise(sample).mul(amplitude));
    sample = sample.mul(2);
    amplitude *= 0.5;
  }

  return value;
};
